import React, { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import Checkbox from "shared/@forms/Checkbox";
import TextArea from "shared/@forms/TextArea";
import AlertNotice from "shared/AlertNotice";
import Button from "shared/Button";
import { Modal } from "shared/Modal";
import ModalActionBar from "shared/ModalActionBar";

import {
  createDreamCastManualRepair,
  lookupDreamCastManualRepairStatus,
} from "./supportOperationsApi";
import styles from "./SupportAccountOperations.module.css";

const MANUAL_REPAIR_TERMINAL_STATUSES = new Set([
  "completed",
  "ineligible",
  "conflict",
  "dead",
  "canceled",
]);
const MANUAL_REPAIR_POLLABLE_STATUSES = new Set([
  "pending",
  "retry-scheduled",
  "processing",
]);
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 6;
const MAX_POLL_DURATION_MS = 20000;

function createInitialState() {
  return {
    isOpen: false,
    reason: "",
    confirmed: false,
    pending: false,
    operation: null,
    error: null,
    retryCandidate: null,
    pollActive: false,
    pollNeedsRefresh: false,
  };
}

function mapSubmitError(status, invalidPayload) {
  if (invalidPayload) {
    return "DreamCast manual repair returned an invalid response. Please retry.";
  }

  if (status === 400) {
    return "Manual repair request was invalid. Please review and try again.";
  }

  if (status === 401 || status === 403) {
    return "You are not authorized to submit a manual repair request.";
  }

  if (status === 404) {
    return "DreamCast manual repair is unavailable for this contact.";
  }

  if (status === 409) {
    return "A conflicting manual repair already exists for this target.";
  }

  if (status === 429) {
    return "Too many manual repair requests. Please try again shortly.";
  }

  if (status === 503) {
    return "Manual repair request could not be confirmed. Retry with the same request.";
  }

  if (typeof status === "number" && status >= 500) {
    return "DreamCast manual repair is unavailable right now.";
  }

  return "Unable to submit manual repair right now.";
}

function mapStatusError(status, invalidPayload) {
  if (invalidPayload) {
    return "Manual repair status response was invalid. Refresh status to retry.";
  }

  if (status === 400) {
    return "Manual repair status request was invalid.";
  }

  if (status === 401 || status === 403) {
    return "You are not authorized to read manual repair status.";
  }

  if (status === 404) {
    return "Manual repair operation was not found.";
  }

  if (status === 409) {
    return "Manual repair status is in conflict. Refresh status to retry.";
  }

  if (status === 429) {
    return "Too many status requests. Please wait and refresh status.";
  }

  if (status === 503) {
    return "Manual repair status is temporarily unavailable. Refresh status to retry.";
  }

  if (typeof status === "number" && status >= 500) {
    return "Manual repair status is unavailable right now.";
  }

  return "Unable to refresh manual repair status right now.";
}

function shouldReuseIdempotencyKeyForAmbiguousResult(result) {
  const status = result?.status;
  if (status == null) {
    return true;
  }

  if (result?.invalidPayload) {
    return true;
  }

  return typeof status === "number" && status >= 500;
}

function buildScope(target) {
  if (target.mode === "contact") {
    return {
      mode: "contact",
      wwgId: target.wwgId,
    };
  }

  return {
    mode: "both",
  };
}

function shouldPollStatus(status) {
  return MANUAL_REPAIR_POLLABLE_STATUSES.has(status);
}

function isTerminalStatus(status) {
  return MANUAL_REPAIR_TERMINAL_STATUSES.has(status);
}

function buildTargetLabel(target) {
  if (target.mode === "contact") {
    return `${target.heading}${
      target.displayName ? ` (${target.displayName})` : ""
    } WWG ID ${target.wwgId}`;
  }

  return `both contacts for IBO ${target.iboNumber}`;
}

export default function ManualRepairOperations({
  target,
  canRepair,
  makeAuthedRequest,
}) {
  const [state, setState] = useState(createInitialState);
  const mountedRef = useRef(true);
  const generationRef = useRef(0);
  const contextRef = useRef({
    identity: "",
    canRepair: false,
  });
  const previousIdentityRef = useRef("");
  const pollTimerRef = useRef(null);
  const pollBudgetRef = useRef({
    startedAt: 0,
    attempts: 0,
    operationId: null,
  });

  const identity = `${target.identityToken}|${
    canRepair ? "enabled" : "disabled"
  }`;
  contextRef.current = {
    identity,
    canRepair,
  };

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const invalidateRequests = useCallback(() => {
    generationRef.current += 1;
    clearPollTimer();
    pollBudgetRef.current = {
      startedAt: 0,
      attempts: 0,
      operationId: null,
    };
  }, [clearPollTimer]);

  const closeModal = useCallback(() => {
    invalidateRequests();
    setState(createInitialState());
  }, [invalidateRequests]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      invalidateRequests();
    };
  }, [invalidateRequests]);

  useEffect(() => {
    const previousIdentity = previousIdentityRef.current;
    previousIdentityRef.current = identity;

    if (!state.isOpen) {
      return;
    }

    if (previousIdentity !== identity) {
      closeModal();
    }
  }, [closeModal, identity, state.isOpen]);

  const isRequestCurrent = useCallback((generation, requestIdentity) => {
    if (!mountedRef.current) {
      return false;
    }

    if (generationRef.current !== generation) {
      return false;
    }

    const latestContext = contextRef.current;
    if (latestContext.identity !== requestIdentity) {
      return false;
    }

    return latestContext.canRepair;
  }, []);

  const pollStatus = useCallback(
    async (
      generation,
      requestIdentity,
      operationId,
      { immediate = false } = {}
    ) => {
      if (!isRequestCurrent(generation, requestIdentity)) {
        return;
      }

      const now = Date.now();
      const elapsedMs = now - pollBudgetRef.current.startedAt;
      if (
        pollBudgetRef.current.attempts >= MAX_POLL_ATTEMPTS ||
        elapsedMs >= MAX_POLL_DURATION_MS
      ) {
        setState((previous) => ({
          ...previous,
          pollActive: false,
          pollNeedsRefresh: true,
        }));
        return;
      }

      if (!immediate) {
        await new Promise((resolve) => {
          pollTimerRef.current = setTimeout(resolve, POLL_INTERVAL_MS);
        });
      }

      if (!isRequestCurrent(generation, requestIdentity)) {
        return;
      }

      pollBudgetRef.current.attempts += 1;
      const result = await lookupDreamCastManualRepairStatus(
        makeAuthedRequest,
        operationId
      );

      if (!isRequestCurrent(generation, requestIdentity)) {
        return;
      }

      if (!result.ok) {
        setState((previous) => ({
          ...previous,
          pollActive: false,
          pollNeedsRefresh: true,
          error: mapStatusError(result.status, result.invalidPayload),
        }));
        return;
      }

      setState((previous) => ({
        ...previous,
        operation: result.data,
        error: null,
      }));

      if (isTerminalStatus(result.data.status)) {
        setState((previous) => ({
          ...previous,
          pollActive: false,
          pollNeedsRefresh: false,
        }));
        return;
      }

      const elapsedAfterResponse = Date.now() - pollBudgetRef.current.startedAt;
      if (
        pollBudgetRef.current.attempts >= MAX_POLL_ATTEMPTS ||
        elapsedAfterResponse >= MAX_POLL_DURATION_MS
      ) {
        setState((previous) => ({
          ...previous,
          pollActive: false,
          pollNeedsRefresh: true,
        }));
        return;
      }

      await pollStatus(generation, requestIdentity, operationId);
    },
    [isRequestCurrent, makeAuthedRequest]
  );

  const beginPolling = useCallback(
    async (
      generation,
      requestIdentity,
      operationId,
      { immediate = true } = {}
    ) => {
      pollBudgetRef.current = {
        startedAt: Date.now(),
        attempts: 0,
        operationId,
      };

      setState((previous) => ({
        ...previous,
        pollActive: true,
        pollNeedsRefresh: false,
      }));

      await pollStatus(generation, requestIdentity, operationId, {
        immediate,
      });
    },
    [pollStatus]
  );

  const trimmedReason = state.reason.trim();
  const isReasonValid =
    trimmedReason.length >= 1 && trimmedReason.length <= 512;

  const submitManualRepair = async () => {
    if (!canRepair || state.pending || !state.confirmed || !isReasonValid) {
      return;
    }

    const requestIdentity = identity;
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    clearPollTimer();

    const targetFingerprint = `${target.identityToken}|${trimmedReason}`;
    const shouldReuseIdempotencyKey =
      state.retryCandidate?.retryWithSameRequest &&
      state.retryCandidate?.fingerprint === targetFingerprint;

    const idempotencyKey = shouldReuseIdempotencyKey
      ? state.retryCandidate.idempotencyKey
      : uuidv4();

    setState((previous) => ({
      ...previous,
      pending: true,
      error: null,
      pollNeedsRefresh: false,
    }));

    const result = await createDreamCastManualRepair(makeAuthedRequest, {
      iboNumber: target.iboNumber,
      contactScope: buildScope(target),
      reason: trimmedReason,
      idempotencyKey,
    });

    if (!isRequestCurrent(generation, requestIdentity)) {
      return;
    }

    if (!result.ok) {
      const retryWithSameRequest =
        shouldReuseIdempotencyKeyForAmbiguousResult(result);
      setState((previous) => ({
        ...previous,
        pending: false,
        operation: null,
        error: mapSubmitError(result.status, result.invalidPayload),
        retryCandidate: {
          fingerprint: targetFingerprint,
          idempotencyKey,
          retryWithSameRequest,
        },
      }));
      return;
    }

    setState((previous) => ({
      ...previous,
      pending: false,
      operation: result.data,
      error: null,
      retryCandidate: {
        fingerprint: targetFingerprint,
        idempotencyKey,
        retryWithSameRequest: false,
      },
    }));

    if (shouldPollStatus(result.data.status)) {
      await beginPolling(generation, requestIdentity, result.data.operationId, {
        immediate: false,
      });
    }
  };

  const refreshStatus = async () => {
    if (!state.operation || state.pending || !canRepair) {
      return;
    }

    const requestIdentity = identity;
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    clearPollTimer();

    await beginPolling(
      generation,
      requestIdentity,
      state.operation.operationId
    );
  };

  if (!canRepair) {
    return null;
  }

  const targetLabel = buildTargetLabel(target);
  const submitButtonLabel =
    state.retryCandidate?.retryWithSameRequest &&
    state.retryCandidate.fingerprint ===
      `${target.identityToken}|${trimmedReason}`
      ? "Retry request"
      : "Submit repair";

  return (
    <>
      <Button
        size="small"
        purpose="delete"
        onClick={() =>
          setState((previous) => ({
            ...createInitialState(),
            isOpen: true,
            reason: previous.reason,
          }))
        }
        aria-label={
          target.mode === "both"
            ? "Repair both contacts"
            : `Repair ${target.heading}`
        }
      >
        {target.mode === "both" ? "Repair both" : "Repair"}
      </Button>

      <Modal
        title="Manual repair"
        isOpen={state.isOpen}
        onClose={closeModal}
        className={styles.modal}
      >
        <div className={styles.modalContent}>
          <p className={styles.intro}>Target: {targetLabel}</p>

          <label className={styles.field}>
            <span>Reason (1-512 characters)</span>
            <TextArea
              value={state.reason}
              maxLength={512}
              onInput={(event) =>
                setState((previous) => ({
                  ...previous,
                  reason: event.target.value,
                  error: null,
                }))
              }
            />
          </label>

          {!isReasonValid && (
            <AlertNotice title="Reason required">
              <div>Provide a reason between 1 and 512 characters.</div>
            </AlertNotice>
          )}

          <label className={styles.checkboxField}>
            <Checkbox
              checked={state.confirmed}
              disabled={state.pending}
              onChange={(event) =>
                setState((previous) => ({
                  ...previous,
                  confirmed: event.target.checked,
                  error: null,
                }))
              }
            />
            <span>I confirm manual repair for {targetLabel}.</span>
          </label>

          {state.pending && (
            <p role="status" aria-live="polite">
              Submitting manual repair...
            </p>
          )}

          {state.error && (
            <AlertNotice title="Manual repair unavailable">
              <div>{state.error}</div>
            </AlertNotice>
          )}

          {state.operation && (
            <AlertNotice title="Manual repair status">
              <div>Operation: {state.operation.operationId}</div>
              <div>Status: {state.operation.status}</div>
              <div>Coalesced: {state.operation.coalesced ? "Yes" : "No"}</div>
              <div>
                Last updated:{" "}
                {new Date(state.operation.modifiedOn).toLocaleString()}
              </div>
            </AlertNotice>
          )}

          {state.pollActive && (
            <p role="status" aria-live="polite">
              Refreshing status...
            </p>
          )}

          {state.pollNeedsRefresh && (
            <AlertNotice title="Status refresh paused">
              <div>Automatic polling reached its safety limit.</div>
              <div>Use Refresh status for a bounded follow-up check.</div>
            </AlertNotice>
          )}

          <ModalActionBar>
            <Button onClick={closeModal}>Close</Button>
            {state.operation && !isTerminalStatus(state.operation.status) && (
              <Button
                onClick={refreshStatus}
                disabled={state.pending || state.pollActive}
              >
                Refresh status
              </Button>
            )}
            <Button
              purpose="delete"
              onClick={submitManualRepair}
              disabled={state.pending || !state.confirmed || !isReasonValid}
            >
              {submitButtonLabel}
            </Button>
          </ModalActionBar>
        </div>
      </Modal>
    </>
  );
}
